import { SearchNodeKind } from "client/Api/SearchSettings/searchSettings";
import {
	SearchNodeModel,
	GlobalSearchScopeKind,
	ScopeSearchNodeModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { Toolbar } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { ReactElement } from "react";

export type GlobalSearchNodeTabsProps = {
	selectedNode: SearchNodeModel | null;
	nodes: readonly SearchNodeModel[];
	onSelectedNodeChange: (node: SearchNodeModel | null) => void;
	trackingId: string;
};

export const GlobalSearchNodeTabs = (props: GlobalSearchNodeTabsProps): ReactElement => {
	const {
		selectedNode,
		nodes,
		onSelectedNodeChange,
		trackingId
	} = props;

	return (
		<Toolbar
			trackingId={trackingId}
		>
			<Toolbar.Button
				kind={!selectedNode
					? "primary"
					: "secondary"
				}
				text={localize("_Top")}
				onClick={() => onSelectedNodeChange(null)}
				trackingId="top-result-tab"
				aria-current={!selectedNode}
			/>
			{nodes.map(
				x => {
					const key: string = getSearchNodeUniqueId(x);
					const isSelected: boolean = selectedNode
						? isNodeSelected(x, selectedNode)
						: false;

					return (
						<Toolbar.Button
							key={key}
							kind={isSelected
								? "primary"
								: "secondary"
							}
							text={x.name}
							trackingId={key}
							onClick={() => onSelectedNodeChange(x)}
							responsiveBehavior={isSelected
								? "always-visible"
								: undefined
							}
							aria-current={isSelected}
						/>
					);
				}
			)}
		</Toolbar>
	);
};
GlobalSearchNodeTabs.displayName = "GlobalSearchNodeTabs";

export function getSearchNodeUniqueId(
	node: SearchNodeModel
): string {
	switch(node.kind) {
		case SearchNodeKind.Group:
			return `group|${node.id}`;
		case SearchNodeKind.Scope:
			return getSearchScopeUniqueId(node);
		default:
			never(node);
	}
}

export function getSearchScopeUniqueId(
	scope: ScopeSearchNodeModel
): string {
	switch(scope.scopeKind) {
		case GlobalSearchScopeKind.IntegrationFlow:
			return `integration-flow|${scope.integrationId}`;
		case GlobalSearchScopeKind.Predefined:
			return `predefined|${scope.predefinedKind}`;
		default:
			never(scope);
	}
}

export function isNodeSelected(
	node: SearchNodeModel,
	selectedNode: SearchNodeModel
): boolean {
	if (areTheSameNodes(node, selectedNode)) {
		return true;
	}

	return node.kind === SearchNodeKind.Group
		&& node
			.scopes
			.some((x) => areTheSameNodes(x, selectedNode));
}

function areTheSameNodes(
	nodeA: SearchNodeModel,
	nodeB: SearchNodeModel
): boolean {
	if (nodeA.kind === SearchNodeKind.Group
		&& nodeB.kind === SearchNodeKind.Group
	) {
		return nodeA.id === nodeB.id;
	}

	if (nodeA.kind === SearchNodeKind.Scope
		&& nodeB.kind === SearchNodeKind.Scope
	) {
		if (nodeA.scopeKind === GlobalSearchScopeKind.IntegrationFlow
			&& nodeB.scopeKind === GlobalSearchScopeKind.IntegrationFlow
		) {
			return nodeA.integrationId === nodeB.integrationId;
		}

		if (nodeA.scopeKind === GlobalSearchScopeKind.Predefined
			&& nodeB.scopeKind === GlobalSearchScopeKind.Predefined
		) {
			return nodeA.predefinedKind === nodeB.predefinedKind;
		}
	}

	return false;
}